import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Modal,
  Toolbar,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import FullScreenLoadingIndicator from "../../UI/FullScreenLoadingIndicator";
import ErrorBox from "../../Util/ErrorBox";
import MessageBox from "../../Util/MessageBox";
import { AdminAction } from "../../../store/Admin/Admin-slice";
import { useMemo, useState } from "react";
import ColoredTextField from "../../UI/ColoredTextField";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import useHttp from "../../../Hooks/use-http";
import { APIURL, OSAltKey } from "../../Util/Util";
import AutoCompleteUsers from "../../UI/AutoCompleteUsers";
import AutoCompleteICAT from "../../UI/AutoCompleteICAT";
import ConfirmationBox from "../../Util/ConfirmationBox";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "90%", md: "90%", lg: "80%", xl: "80%" },
  bgcolor: "background.paper",
  boxShadow: 24,
  maxHeight: "80%",
  overflowY: "auto",
  p: 4,
  pb: 2,
};

let inputTimeOut;
let inputTimeOutiCAT;

const AddEditAssignICATModal = ({ getAssignICATTableData }) => {
  const dispatch = useDispatch();
  const currentAssignICATInfo = useSelector(
    (state) => state.adminState.currentAssignICATInfo
  );
  const defaultAssignICATInfo = useSelector(
    (state) => state.adminState.defaultAssignICATInfo
  );

  const accessToken = useSelector(
    (state) => state.authenticationState.accessToken
  );

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: "Bearer " + accessToken,
    }),
    [accessToken]
  );

  const open = useSelector((state) => state.adminState.openAssignICAT);
  const isEdit = useSelector((state) => state.adminState.isEdit);
  const [errorMsg, setErrorMsg] = useState("");
  const [ErrorState, setErrorState] = useState(false);
  const [duplicateErrorState, setDuplicateErroState] = useState(false);

  const [openMessageBox, setOpenMessageBox] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [openConfirmApply, setOpenConfirmApply] = useState(false);

  const { sendRequest: fetchData2, isLoading: isLoadingUsersOptions } =
    useHttp();
  const { sendRequest: fetchICATOptions, isLoading: isLoadingICATOptions } =
    useHttp();
  const {
    sendRequest: fetchUserOrgOptions,
    isLoading: isLoadingUserOrgOptions,
  } = useHttp();
  const { sendRequest: saveAssignICATs, isLoading: isLoadingSaveAssignICATs } =
    useHttp();
  const {
    sendRequest: fetchDuplicateOrgICAT,
    isLoading: isLoadingDuplicateOrgICAT,
  } = useHttp();

  const handleSaveAssignICAT = () => {
    const assignICATs = currentAssignICATInfo.map((row) => ({
      id: isEdit ? row.id : undefined,
      ntuser: row.user.ntuser,
      org: row.org,
      oracleID: row.oracleID,
      legacy: row.legacy,
      username: row.user.username,
      telnet: row.telnet,
      icat: row.iCAT.icat,
    }));
    const body = {
      assignICATs,
    };
    console.log("body", body);
    saveAssignICATs(
      {
        url: `${APIURL}admin/saveMultiAssignICAT`,
        headers,
        method: "POST",
        body,
      },
      (data) => {
        setResultMessage(data.Success);
        setOpenMessageBox(true);
        dispatch(AdminAction.resetAssignICAT());
        dispatch(AdminAction.resetSelectedRowsAssignICAT());
        getAssignICATTableData();
      },
      (error) => {
        console.log(error);
        setErrorMsg(
          `ERROR: Unable to save Assign ICATs, please re-open this popup and try again. ${error}`
        );
        setErrorState(true);
      }
    );
  };

  const validateFields = () => {
    let isValid = true;
    const userSet = new Set();
    let duplicateUser = false;

    currentAssignICATInfo.forEach((row) => {
      let isUserError = false,
        isOrgError = false,
        isICATError = false;
      if (!row.user) {
        isUserError = true;
        isValid = false;
      }
      if (!row.org) {
        isOrgError = true;
        isValid = false;
      }
      if (!row.iCAT) {
        isICATError = true;
        isValid = false;
      }
      let uniqueKey = `${row?.user?.useremail}-${row.org}-${row?.iCAT?.icat}`;
      if (userSet.has(uniqueKey)) {
        isUserError = true;
        isICATError = true;
        isOrgError = true;
        isValid = false;
        duplicateUser = true;
      } else {
        userSet.add(uniqueKey);
      }

      dispatch(
        AdminAction.setAssignICATInfo({
          row,
          value: { isUserError, isOrgError, isICATError },
          type: "error",
        })
      );
    });
    if (duplicateUser) {
      setErrorMsg("The combination of User, Org and ICAT should be unique!");
      setErrorState(true);
    }

    return isValid;
  };

  const handleAddMoreFields = () => {
    if (currentAssignICATInfo.length > 0) {
      const newId =
        currentAssignICATInfo[currentAssignICATInfo.length - 1].id + 1;
      // Copy values from the first row
      const firstRow = currentAssignICATInfo[0];
      dispatch(
        AdminAction.addAssignICAT([
          ...currentAssignICATInfo,
          {
            ...defaultAssignICATInfo,
            id: newId,
            user: firstRow.user,
            username: firstRow.username,
            userid: firstRow.userid,
            useremail: firstRow.useremail,
            org: firstRow.org,
            userOptions: firstRow.userOptions,
            orgOptions: firstRow.orgOptions,
            error: {},
          },
        ])
      );
    } else {
      dispatch(AdminAction.addAssignICAT([defaultAssignICATInfo]));
    }
  };

  const getUserOrgList = (userid, row) => {
    fetchUserOrgOptions(
      { url: `${APIURL}admin/userOrgList?filterOn=${userid}`, headers },
      (data) => {
        const orgOptions = data?.USER_ORG_LIST?.map((org) => {
          return org.user_org_c;
        });
        dispatch(
          AdminAction.setAssignICATInfo({
            row,
            value: orgOptions,
            type: "orgOptions",
          })
        );
      },
      (error) => {
        console.log(`Unable to retrieve User Org Options, Error: ${error}`);
        setErrorMsg(
          `ERROR: Unable to retrieve User Org Options, please try refresh the page. Error: ${error}`
        );
        setErrorState(true);
      }
    );
  };

  const removeInputBox = (row) => {
    dispatch(AdminAction.removeInputBoxAssignICAT({ row }));
  };

  const handleUserInputChange = (newValue, name, reason, row) => {
    clearTimeout(inputTimeOut);
    inputTimeOut = setTimeout(() => {
      if (newValue.length > 2 && reason === "input") {
        console.log("queried " + newValue);
        fetchData2(
          {
            url: `${APIURL}admin/usersList/?filterOn=${newValue}`,
            headers,
          },
          (data) => {
            const usersList = data.USERS_LIST.sort();
            dispatch(
              AdminAction.setAssignICATInfo({
                row,
                value: usersList,
                type: "userOptions",
              })
            );
          },
          (error) => {
            console.log("ERROR: ", error);
            setErrorMsg(`ERROR: Unable to retrieve name, ${error}`);
            setErrorState(true);
          }
        );
      }
    }, 500);
  };

  const handleiCATInputChange = (newValue, name, reason, row) => {
    clearTimeout(inputTimeOutiCAT);

    inputTimeOut = setTimeout(() => {
      if (newValue?.length > 2 && reason === "input") {
        console.log("queried " + newValue);
        fetchICATOptions(
          {
            url: `${APIURL}admin/iCATList/?filterOn=${newValue}`,
            headers,
          },
          (data) => {
            const iCATList = data.ICAT_LIST.sort();

            const transformedSelections = iCATList.map((item) => ({
              id: item.icat_id,
              label: item.icat,
              ...item,
            }));

            dispatch(
              AdminAction.setAssignICATInfo({
                row,
                value: transformedSelections,
                type: "iCATOptions",
              })
            );
          },
          (error) => {
            console.log("ERROR: ", error);
            setErrorMsg(`ERROR: Unable to retrieve iCAT, ${error}`);
            setErrorState(true);
          }
        );
      }
    }, 500);
  };

  const checkDuplicateAssignICAT = (row, icatObj, isEdit) => {
    console.log("checkDuplicateAssignICAT:", row, icatObj, isEdit);

    fetchDuplicateOrgICAT(
      {
        url: `${APIURL}admin/checkDuplicateAssignICAT`,
        method: "POST",
        headers,
        body: { org: row.org, oracleID: icatObj?.icat_id },
      },
      (data) => {
        if (data.duplicates && data.duplicates.length > 0) {
          const filteredDuplicates = isEdit
            ? data.duplicates?.filter(
                (d) => d.id !== row.id || d.ntuser !== "_To be determined"
              )
            : data.duplicates?.filter((d) => d.ntuser !== "_To be determined");
          console.log("filteredDuplicates: ", filteredDuplicates);

          if (filteredDuplicates && filteredDuplicates.length > 0) {
            const userList = (
              <ul style={{ paddingLeft: "20px", marginTop: "8px" }}>
                {filteredDuplicates.map((user, index) => (
                  <li key={index}>
                    {user.username} &lt;{user.ntuser}&gt;
                  </li>
                ))}
              </ul>
            );

            const errorMessage = (
              <>
                <strong>Notice:</strong>
                <br/>
                iCAT <span style={{fontWeight: "bold", display: "contents"}}>{icatObj.icat}</span> is already assigned to
                another materials engineer{filteredDuplicates.length > 1 ? "s" : ""} for Org <span style={{fontWeight: "bold", display: "contents"}}>{row.org}</span>:{userList}
              </>
            );

            setErrorMsg(errorMessage);
            setDuplicateErroState(true);
            setErrorState(true);
          } else {
            setErrorMsg("");
            setErrorState(false);
          }
        }
      },
      (error) => {
        setErrorMsg(`ERROR: Unable to check duplicates. ${error}`);
        setErrorState(true);
      }
    );
  };

  const handleInputChange = (row, type) => (event, newValue) => {
    const value =
      type === "telnet"
        ? event.target.value
        : type === "org"
        ? newValue
        : newValue;
    dispatch(AdminAction.setAssignICATInfo({ row, value, type }));
    if (type === "user" && newValue) {
      getUserOrgList(newValue?.userid, row);
    }
    // Check for duplicate when ICAT is selected and org is present
    if (type === "iCAT" && newValue && row.org) {
      console.log("icat:", type, newValue, row.org);

      checkDuplicateAssignICAT(row, newValue, isEdit);
    }
  };

  // const Message = () => {
  //   return (
  //     <Stack>
  //       <Typography>{"Summary of new changes"}</Typography>
  //       <Paper sx={{ height: 300, overflow: "auto" }}>
  //         <List disablePadding component="div" role="list">
  //           {resultMessage.map((value, index) => {
  //             const labelId = `transfer-list-item-${value}-label`;
  //             return (
  //               <ListItem key={index} role="listitem" divider>
  //                 <ListItemText id={labelId} primary={value} sx={{ m: 0 }} />
  //               </ListItem>
  //             );
  //           })}
  //         </List>
  //       </Paper>
  //     </Stack>
  //   );
  // };

  const messageBoxHandler = () => {
    setOpenMessageBox(false);
    dispatch(AdminAction.setOpenAssignICAT(false));
  };

  const handleApplyClicked = () => {
    if (validateFields()) {
      setOpenConfirmApply(true);
    }
  };

  const handleApplyConfirmed = () => {
    setOpenConfirmApply(false);
    handleSaveAssignICAT();
  };

  const handleCancelClicked = () => {
    dispatch(AdminAction.resetAssignICAT());
    dispatch(AdminAction.setOpenAssignICAT(false));
  };

  return (
    <Box>
      <FullScreenLoadingIndicator
        loading={isLoadingSaveAssignICATs || isLoadingDuplicateOrgICAT}
      />
      <ErrorBox
        msg={errorMsg}
        setErrorState={setErrorState}
        ErrorState={ErrorState}
        buttonText={duplicateErrorState ? 'Continue': null}
      />
      <MessageBox
        open={openMessageBox}
        message={resultMessage}
        messageBoxHandler={messageBoxHandler}
      />
      <ConfirmationBox
        open={openConfirmApply}
        handleYes={handleApplyConfirmed}
        handleNo={() => setOpenConfirmApply(false)}
        message="Save User Assign ICATs?"
      />
      <Modal
        open={open}
        aria-labelledby="Admin-modal-Change-Org"
        aria-describedby="Admin-modal-Change-user-Org"
        disableScrollLock
      >
        <Box
          sx={style}
          tabIndex={0} // <-- Make Box focusable to receive key events
          onKeyDown={(e) => {
            if (e.altKey && e.code === "KeyC") {
              e.preventDefault();
              handleCancelClicked();
            } else if (e.altKey && e.code === "KeyA") {
              e.preventDefault();
              handleApplyClicked();
            }
          }}
        >
          <AppBar
            elevation={0}
            sx={{ position: "fixed", backgroundColor: "#E90029" }}
          >
            <Toolbar>
              <Typography variant="h6" component="div" color="#fff">
                Admin - {isEdit ? "Edit" : "Add"} Assign ICAT
              </Typography>
            </Toolbar>
          </AppBar>
          <Grid
            mb={4}
            mt={6}
            container
            direction="row"
            justifyContent="start"
            alignItems="flex-start"
            // rowSpacing={1}
            // columnSpacing={1}
            // wrap={"wrap"}
          >
            {currentAssignICATInfo.map((row) => {
              return (
                <Grid
                  key={row.id}
                  mb={1}
                  mt={1}
                  container
                  direction="row"
                  justifyContent="start"
                  alignItems="flex-start"
                  rowSpacing={2}
                  columnSpacing={1}
                  wrap={"wrap"}
                >
                  <Grid item xl={3} lg={3} md={3} sm={5} xs={12}>
                    <FormControl fullWidth error={row.error?.isUserError}>
                      <AutoCompleteUsers
                        row={row}
                        value={
                          JSON.stringify(defaultAssignICATInfo) ===
                          JSON.stringify(row)
                            ? ""
                            : row?.user || ""
                        }
                        required={true}
                        label="User"
                        error={row.error?.isUserError}
                        onChange={handleInputChange(row, "user")}
                        handleUserInputChange={handleUserInputChange}
                        options={row.userOptions || []}
                        isLoading={isLoadingUsersOptions}
                        name={"user"}
                        helperText={"*Insert more than 3 characters to search"}
                      />
                      <FormHelperText>
                        {row.error?.isUserError ? "" : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xl={1.5} lg={1.5} md={1.25} sm={3} xs={12}>
                    {/* <FormControl focused fullWidth size="small"> */}
                    {/* <InputLabel id="user-name">Org</InputLabel> */}
                    <Autocomplete
                      id="primary-org-autocomplete"
                      options={row?.orgOptions || []}
                      loading={isLoadingUserOrgOptions}
                      disabled={row.userid ? false : true}
                      readOnly={row.userid ? false : true}
                      value={row?.org || null}
                      onChange={handleInputChange(row, "org")}
                      renderInput={(params) => (
                        <ColoredTextField
                          error={row.error?.isOrgError}
                          focused
                          required
                          {...params}
                          label={"Org"}
                        />
                      )}
                      loadingText={
                        <Box display={"flex"} sx={{ mx: "auto" }}>
                          <CircularProgress color="inherit" size={80} />
                        </Box>
                      }
                    />
                    {/* </FormControl> */}
                  </Grid>
                  <Grid item xl={3.5} lg={3.5} md={4.75} sm={4} xs={12}>
                    <AutoCompleteICAT
                      row={row}
                      required={true}
                      value={
                        JSON.stringify(defaultAssignICATInfo) ===
                        JSON.stringify(row)
                          ? ""
                          : row?.iCAT || ""
                      }
                      label="ICAT"
                      onChange={handleInputChange(row, "iCAT")}
                      onInputChange={handleiCATInputChange}
                      options={row.iCATOptions || []}
                      error={row.error?.isICATError}
                      isLoading={isLoadingICATOptions}
                      name={"iCAT"}
                      helperText={"*Insert more than 3 characters to search"}
                      disabled={isEdit ? true : row.org ? false : true}
                      readOnly={row.org ? false : true}
                    />
                  </Grid>
                  <Grid item xl={2} lg={2} md={1.4} sm={5.7} xs={12}>
                    <ColoredTextField
                      inputProps={{ readOnly: true }}
                      focused
                      fullWidth
                      label={"Oracle ID"}
                      value={row.oracleID}
                    />
                  </Grid>
                  <Grid item xl={1.5} lg={1.5} md={1} sm={5.7} xs={12}>
                    <ColoredTextField
                      focused
                      inputProps={{ readOnly: true }}
                      fullWidth
                      label={"Legacy"}
                      value={row.legacy}
                    />
                  </Grid>
                  {/* <Grid item xl={1.5} lg={1.5} md={2.4} sm={3.8} xs={12}>
                    <ColoredTextField
                      focused
                      fullWidth
                      label={"Telnet"}
                      value={row?.telnet}
                      onChange={handleInputChange(row, "telnet")}
                    />
                  </Grid> */}
                  <Grid item xl={0.5} lg={0.5} md={0.6} sm={0.6} xs={12}>
                    {!isEdit && (
                      <Box sx={{ display: "flex" }}>
                        <Box
                          display={"flex"}
                          // ml={1}
                          justifyContent={"center"}
                          sx={{
                            backgroundColor: "#FF0000",
                            "&:hover": {
                              backgroundColor: "#fc6b6b",
                            },
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => removeInputBox(row)}
                            aria-label="clearSearch"
                          >
                            <HighlightOffIcon style={{ fill: "white" }} />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                    <Divider />
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ display: "flex" }}>
            <Box flex={"1"}>
              {!isEdit && (
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddMoreFields}
                  onKeyDown={(event) =>
                    event.key === "Enter" && event.preventDefault()
                  }
                  sx={{
                    py: 0,
                    borderRadius: 0,
                    backgroundColor: "transparent",
                    textTransform: "none",
                    color: "#000000",
                    fontSize: "1 rem",
                    "& .MuiButton-endIcon": {
                      marginLeft: 0,
                    },
                    "&:hover": {
                      color: "#0f0fff",
                      textTransform: "none",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  Add more user
                </Button>
              )}
            </Box>
            <Box>
              <Button onClick={handleApplyClicked}>
                <u
                  style={{
                    textDecorationThickness: "2.5px",
                    fontWeight: "bold",
                  }}
                >
                  A
                </u>
                pply
              </Button>
              <Button onClick={handleCancelClicked}>
                <u
                  style={{
                    textDecorationThickness: "2.5px",
                    fontWeight: "bold",
                  }}
                >
                  C
                </u>
                ancel
              </Button>
              <Typography
                sx={{ fontSize: "0.7rem" }}
              >{`Shortcut: ${OSAltKey} + Hotkey`}</Typography>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default AddEditAssignICATModal;
